/**
 * AdmissionsEnquiries — call-back requests left on the public school page,
 * worked through by the admissions office.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Phone, Mail, Clock, Globe } from 'lucide-react';

const TONE = {
  new: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-sky-100 text-sky-700',
  awaiting_response: 'bg-violet-100 text-violet-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-600',
};

export const AdmissionsEnquiries = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admissions-enquiries', tenantId, filter],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/enquiries`,
      { params: filter ? { status: filter } : {} })).data,
  });

  const update = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(
      `/school/admissions/tenants/${tenantId}/enquiries/${id}`, body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admissions-enquiries', tenantId] });
      setNoteFor(null);
      setNote('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  const items = data?.items || [];
  const statuses = data?.statuses || [];

  return (
    <div className="space-y-4" data-testid="admissions-enquiries">
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setFilter('')} data-testid="enquiry-filter-all"
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                  filter === '' ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-500 border-slate-200'}`}>
          All
        </button>
        {statuses.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`enquiry-filter-${s}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                    filter === s ? 'bg-slate-900 text-white border-slate-900'
                                 : 'bg-white text-slate-500 border-slate-200'}`}>
            {s.replace('_', ' ')} {data?.counts?.[s] ? `· ${data.counts[s]}` : ''}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500"
                       data-testid="enquiries-empty">
            No enquiries yet. Visitors who ask for a call back on the school page land here.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((e) => (
            <Card key={e.id} data-testid={`enquiry-row-${e.id}`}>
              <CardContent className="py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{e.name}</span>
                      <Badge className={`border-0 text-[10px] ${TONE[e.status] || TONE.closed}`}>
                        {e.status.replace('_', ' ')}
                      </Badge>
                      {e.from_public_page && (
                        <Badge variant="outline" className="text-[10px]">
                          <Globe className="h-3 w-3 mr-1" /> School page
                        </Badge>
                      )}
                      {e.reference && (
                        <span className="text-[10px] font-mono text-slate-400">{e.reference}</span>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 hover:text-slate-900">
                        <Phone className="h-3 w-3" /> {e.phone}
                      </a>
                      {e.email && (
                        <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-slate-900">
                          <Mail className="h-3 w-3" /> {e.email}
                        </a>
                      )}
                      {e.best_time && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3 w-3" /> {e.best_time}
                        </span>
                      )}
                      {e.programme_name && <span>Interested in {e.programme_name}</span>}
                    </div>
                    {e.message && <p className="mt-2 text-sm text-slate-600">{e.message}</p>}
                    {e.notes && (
                      <p className="mt-1.5 text-xs text-slate-500 italic">Note: {e.notes}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <select value={e.status} data-testid={`enquiry-status-${e.id}`}
                            onChange={(ev) => update.mutate({ id: e.id, body: { status: ev.target.value } })}
                            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
                      {statuses.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                    <Button variant="ghost" size="sm" data-testid={`enquiry-note-btn-${e.id}`}
                            onClick={() => { setNoteFor(e.id); setNote(e.notes || ''); }}>
                      Note
                    </Button>
                  </div>
                </div>

                {noteFor === e.id && (
                  <div className="mt-3 flex gap-2">
                    <Input value={note} onChange={(ev) => setNote(ev.target.value)}
                           data-testid={`enquiry-note-input-${e.id}`}
                           placeholder="What came out of the call?" />
                    <Button size="sm" data-testid={`enquiry-note-save-${e.id}`}
                            onClick={() => update.mutate({ id: e.id, body: { notes: note } })}>
                      Save
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdmissionsEnquiries;
